import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useSnackbar } from "notistack";

import { useAuth } from "/imports/state/AuthContext";
import { useWidget } from "/imports/state/WidgetContext";
import { useUrlFilters } from "/imports/libs/url";
import { PhoneField } from "/imports/ui/components/shared";
import api from "/imports/api/client";

function OnboardingView() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { slug, saasId } = useWidget();
  const { user, client, refreshUser, isAuthenticated, loading: authLoading } = useAuth();
  const { filters } = useUrlFilters({});

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // /login returns a slim user without profile; pull the full record so the
  // form can prefill firstName/lastName/phone.
  useEffect(() => {
    if (!authLoading && isAuthenticated && !user?.profile) {
      refreshUser();
    }
  }, [authLoading, isAuthenticated, user, refreshUser]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate(`/${slug}/login?redirect=onboard`, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, slug]);

  // Already a client — nothing to do here.
  useEffect(() => {
    if (client) {
      const redirect = filters.redirect || "";
      navigate(`/${slug}/${redirect}`, { replace: true });
    }
  }, [client, filters.redirect, navigate, slug]);

  const profile = user?.profile || {};
  const firstName = profile.firstName || "";
  const lastName = profile.lastName || "";
  const email = user?.email || profile.email || "";
  const phone = profile.phoneNumber || "";

  const handleJoin = async () => {
    if (!acceptTerms) {
      setError("Please accept the terms and conditions to continue");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.onboard(saasId);
      await refreshUser();
      enqueueSnackbar("Successfully joined! You can now book services.", { variant: "success" });
      const redirect = filters.redirect || "";
      if (result?.preferencesToken) {
        navigate(`/${slug}/preferences`, { replace: true, state: { preferencesToken: result.preferencesToken, afterSave: "home", redirect } });
      } else {
        navigate(`/${slug}/${redirect}`, { replace: true });
      }
    } catch (err) {
      setError(err.message || "Failed to join this facility. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" py={4}>
      <Card sx={{ maxWidth: "32.5rem", width: "100%" }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 }, "&:last-child": { pb: { xs: 2.5, sm: 4 } } }}>
          <Typography variant="h5" fontWeight={600} textAlign="center" gutterBottom>
            Complete Registration
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center" mb={3}>
            You're signed in to Apointa, but you need to join this facility to book services.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
            Your Profile
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" mb={1.5}>
            These come from your Apointa account and can't be changed here.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="First Name" value={firstName} disabled />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Last Name" value={lastName} disabled />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email" value={email} disabled />
            </Grid>
            <Grid item xs={12}>
              <PhoneField
                label="Phone Number"
                value={phone}
                disabled
                helperText={phone ? undefined : "No phone number on your Apointa account — the club may ask you for one."}
              />
            </Grid>
          </Grid>

          <FormControlLabel
            sx={{ mt: 2, alignItems: "flex-start" }}
            control={<Checkbox checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} sx={{ pt: 0 }} />}
            label={<Typography variant="body2">I agree to become a client of this facility and accept the terms and conditions</Typography>}
          />

          <Button
            type="button"
            variant="contained"
            fullWidth
            size="large"
            disabled={submitting || !acceptTerms}
            sx={{ mt: 3, mb: 3 }}
            onClick={handleJoin}
          >
            {submitting ? "Joining..." : "Join this facility"}
          </Button>

          <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
            Member Benefits
          </Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            {["Book classes and facilities online", "Manage your appointments in one place", "Receive updates and offers from this facility"].map(
              (benefit) => (
                <Box key={benefit} display="flex" alignItems="center" gap={1}>
                  <CheckCircleOutlineIcon fontSize="small" color="primary" />
                  <Typography variant="body2">{benefit}</Typography>
                </Box>
              )
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default OnboardingView;
